import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { expVal } from '@atlassian/jira-feature-experiments';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useSubscribeToFavorite } from '@atlassian/jira-favourite-change-provider/src/common/utils/favorite-pub-sub/index.tsx';
import type { FavoriteSubscriptionPayload } from '@atlassian/jira-favourite-change-provider/src/common/utils/favorite-pub-sub/types.tsx';
import type { syncChangeFavouritesMutation } from '@atlassian/jira-relay/src/__generated__/syncChangeFavouritesMutation.graphql.ts';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

/*
Hook to trigger a mutation to reorder the projects in the sidebar when a project is starred.
This is used to ensure that the newly starred project is placed at the top of the custom order.
*/
function useSyncChangeFavouritesConditionally() {
	const cloudId = useCloudId();

	const [commitMutation] = useMutation<syncChangeFavouritesMutationWithNewReorderMode>(graphql`
		mutation syncChangeFavouritesMutation($input: JiraReorderSidebarMenuItemInput!) {
			jira_reorderProjectsSidebarMenuItem(input: $input) @optIn(to: "JiraSidebarMenu") {
				id
				favourites(first: 15) {
					__id
					pageInfo {
						hasNextPage
					}
					edges {
						node {
							id
							projectKey: key
							...Nav4ProjectMenuItem
						}
					}
				}
			}
		}
	`);

	const moveFavouriteToTop = useCallback(
		(payload: FavoriteSubscriptionPayload) => {
			// Prevent optimistic updates from triggering setting custom order
			if (payload.type === 'MUTATION' && payload.isOptimistic) {
				return;
			}
			if (payload.isFavourite) {
				const itemId = JiraProjectAri.parse(payload.entityId).projectId;
				commitMutation({
					variables: {
						input: {
							cloudId,
							reorderMode: 'MOVE_TO_TOP',
							menuItem: {
								itemId,
							},
						},
					},
				});
			}
		},
		[cloudId, commitMutation],
	);

	useSubscribeToFavorite('JiraProject', moveFavouriteToTop);
}

export const useSyncChangeFavourites = functionWithCondition(
	() => expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false),
	useSyncChangeFavouritesConditionally,
	() => null,
);

// extend generated type to include input field favouriteOrder until AGG schema is updated
type syncChangeFavouritesMutationWithNewReorderMode = Omit<
	syncChangeFavouritesMutation,
	'variables'
> & {
	variables: {
		input: {
			cloudId: string;
			reorderMode: 'BEFORE' | 'AFTER' | 'MOVE_TO_TOP' | 'MOVE_TO_BOTTOM' | 'MOVE_UP' | 'MOVE_DOWN';
			menuItem: {
				itemId: string;
			};
		};
	};
};
